import { Input } from '@aigc/antd';
import { NonEmpty } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import type { Pagination, UUIDString } from '@aigc/types';
import {
  EyeOutlined,
  FileTextOutlined,
  ForkOutlined,
  PlusOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Form,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
} from 'react';
import { useForm } from 'react-hook-form';
import { number, object, string, tuple, type InferType } from 'yup';
import { AddScriptModal } from '../../../components/add-script-modal';
import { FormControl } from '../../../components/form-control';
import { TagSelectorModal } from '../../../components/tag-selector-modal';
import { useFissionPageData } from '../../../features/fission/store';
import {
  getScriptsQueryOptions,
  updateScriptMutation,
} from '../../../features/script/api';
import type {
  ScriptDto,
  ScriptListItemDto,
} from '../../../features/script/script.dto';
import { TagList } from '../../../features/tag/components/tag-list';
import { useVideoAnalysisPageData } from '../../../features/video-analysis/store';
import { ScriptStatus } from '../../script/model';
import { GenerationType } from '../../video-model/model-spec';
import { NavigateButton } from '../../video/components/navigate-button';
import { todayRange } from '../constants';
import { ScriptDetailDrawer } from './script-detail-drawer';
import { TimeRangePicker } from './time-range-picker';

const querySchema = object({
  product_name: string().nullable(),
  account_name: string().nullable(),
  time_range: tuple([number().required(), number().required()]).required(),
});

type QueryFormValues = InferType<typeof querySchema>;
type QueryParams = Omit<QueryFormValues, 'time_range'> & {
  start_time?: number;
  end_time?: number;
};

export const ScriptHistory = (props: {
  ref?: Ref<{ refresh: () => void }>;
  admin?: boolean;
  defaultPagination?: Partial<Pagination>;
  onPaginationChange?: (pagination: Pagination) => void;
}) => {
  const { message } = App.useApp();
  const [openOverlay] = useOverlay<boolean>();
  const [selectedTagIds, setSelectedTagIds] = useState<UUIDString[]>([]);
  const showTimeRange = props.admin ?? false;
  const [_2, setVideoAnalysisPageData] = useVideoAnalysisPageData();
  const [_3, setFissionPageData] = useFissionPageData();
  const navigate = useNavigate();
  const updateScript = useMutation(updateScriptMutation);

  const handleViewScript = useCallback(
    (script: ScriptListItemDto) => {
      openOverlay(<ScriptDetailDrawer script={script} admin={props.admin} />);
    },
    [openOverlay, props.admin],
  );

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(querySchema),
    defaultValues: {
      time_range: [...todayRange],
    },
  });

  const { pagination, queryMany, queryParams, changePageNumber } =
    usePagingQueryHelper<QueryParams>({
      defaultPagination: {
        page: props.defaultPagination?.page ?? 1,
        size: props.defaultPagination?.size ?? 10,
      },
      defaultQueryParams: {
        product_name: null,
        start_time: showTimeRange ? todayRange[0] : undefined,
        end_time: showTimeRange ? todayRange[1] : undefined,
      },
      onRefresh: () => {
        scriptListQueryResult.refetch();
      },
    });
  const scriptListQueryResult = useQuery({
    ...getScriptsQueryOptions(props.admin ?? false, pagination, {
      status: ScriptStatus.成功,
      start_time: queryParams?.start_time,
      end_time: queryParams?.end_time,
      product_name: queryParams?.product_name ?? void 0,
      account_name: queryParams?.account_name ?? void 0,
    }),
    placeholderData: keepPreviousData,
    select: (data) => {
      if (!selectedTagIds.length) {
        return data;
      }

      return {
        ...data,
        history: data.history.filter((item) => {
          const { tags } = item;

          if (!tags?.length) {
            return false;
          }

          return selectedTagIds.every((selectedTagId) =>
            tags.some((tag) => tag.id === selectedTagId),
          );
        }),
      };
    },
  });

  useImperativeHandle(props.ref, () => ({
    refresh: () => scriptListQueryResult.refetch(),
  }));

  // 打开标签管理弹窗
  const handleChangeTags = useCallback(
    (script: ScriptListItemDto) => {
      openOverlay(
        <TagSelectorModal
          initialTagIds={script.tags?.map((item) => item.id)}
          beforeConfirm={async (tags) => {
            await updateScript.mutateAsync({
              script_id: script.id,
              tags: tags ?? [],
            });
            message.success('保存成功');
          }}
        />,
      );
    },
    [message, openOverlay],
  );

  // 查看原分析页面
  const handleViewSource = useCallback(
    (script: ScriptListItemDto) => {
      setVideoAnalysisPageData(script);
      navigate({ to: `/video/analysis` });
    },
    [navigate, setVideoAnalysisPageData],
  );

  // 脚本裂变
  const handleFission = useCallback(
    async (script: ScriptListItemDto) => {
      const script_data = script.data;

      const processedData: ScriptDto = {
        id: script_data.id,
        size: script_data.size,
        aspect_ratio: script_data.aspect_ratio,
        resolution: script_data.resolution,
        duration: script_data.duration,
        prompt: script_data.prompt,
        segments: script_data.segments,
        file_list: script_data.file_list,
        extra_prompt: script_data.extra_prompt,
        generation_type: GenerationType.Frame,
        product_name: script_data.product_name || '',
        product_description: script_data.product_description || '',
        product_image_filename: script_data.product_image_filename,
        target_region: script_data.target_region || '',
        fission: script_data.fission,
        context: script_data.context,
        filename: script_data.filename,
        regional_features: script_data.regional_features,
        title: script_data.title,
        user_profile: script_data.user_profile,
        source: void 0,
        generate_audio: script_data.generate_audio,
      };

      setFissionPageData(processedData);

      navigate({ to: '/fission' });
    },
    [navigate, setFissionPageData],
  );

  const columns = useMemo(() => {
    const _columns: TableColumnsType<ScriptListItemDto> = [
      {
        render: (_, script) => {
          return (
            <div className="space-y-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold">
                    产品名称：{script.data.product_name}
                  </span>
                  {script.tags?.map((item) => (
                    <Tag key={item.id} color="magenta">
                      {item.name}
                    </Tag>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <NonEmpty
                  value={script.data.duration}
                  render={(value) => (
                    <span key="duration">视频时长：{value as number}s</span>
                  )}
                />
                <NonEmpty
                  value={script.data.aspect_ratio}
                  render={(value) => (
                    <span key="aspect_ratio">宽高比：{value as string}</span>
                  )}
                ></NonEmpty>
                {props.admin && (
                  <NonEmpty
                    value={script.creator?.name}
                    render={(value) => (
                      <span key="account_name">创建人： {value as string}</span>
                    )}
                  ></NonEmpty>
                )}
                <NonEmpty
                  value={script.created_at}
                  render={(value) => (
                    <span key="created_at">
                      创建时间： {new Date(value as number).toLocaleString()}
                    </span>
                  )}
                ></NonEmpty>
              </div>
              <div className="sticky top-0 z-50 -m-4 flex flex-none flex-wrap items-center gap-2 bg-white px-4 py-2">
                <Button
                  variant="filled"
                  color="default"
                  icon={<EyeOutlined />}
                  disabled={!script.data.segments?.length}
                  onClick={() => {
                    handleViewSource(script);
                  }}
                >
                  查看解析记录
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  icon={<ForkOutlined />}
                  onClick={() => {
                    handleFission(script);
                  }}
                >
                  裂变脚本
                </Button>
                <NavigateButton script={script.data}></NavigateButton>
                <Button
                  variant="filled"
                  color="default"
                  icon={<FileTextOutlined />}
                  onClick={() => {
                    handleViewScript(script);
                  }}
                >
                  查看脚本
                </Button>
                <Button
                  variant="filled"
                  color="default"
                  icon={<TagOutlined />}
                  onClick={() => {
                    handleChangeTags(script);
                  }}
                >
                  管理标签
                </Button>
              </div>
              {!!script.data.prompt.trim().length && (
                <Alert
                  type="info"
                  classNames={{ title: 'whitespace-pre-wrap' }}
                  title={
                    <Typography.Paragraph
                      ellipsis={{ rows: 10, expandable: true }}
                    >
                      {script.data.prompt}
                    </Typography.Paragraph>
                  }
                ></Alert>
              )}
            </div>
          );
        },
      },
    ];

    return _columns;
  }, [
    handleViewScript,
    handleViewSource,
    handleFission,
    handleChangeTags,
    props.admin,
  ]);

  const onQuery = handleSubmit((values) => {
    queryMany({
      product_name: values.product_name ?? void 0,
      account_name: values.account_name,
      start_time: showTimeRange ? values.time_range[0] : undefined,
      end_time: showTimeRange ? values.time_range[1] : undefined,
    });
    props.onPaginationChange?.({ page: 1, size: pagination.size });
  });

  const handleReset = () => {
    const resetTimeRange: [number, number] = [...todayRange];
    reset();
    queryMany({
      product_name: undefined,
      account_name: undefined,
      start_time: showTimeRange ? resetTimeRange[0] : undefined,
      end_time: showTimeRange ? resetTimeRange[1] : undefined,
    });
    props.onPaginationChange?.({ page: 1, size: pagination.size });
  };

  return (
    <div className="space-y-6">
      <Table
        rowKey={(row) => row.id}
        title={() => (
          <div className="space-y-2">
            <div className="flex items-center">
              <Form layout="inline">
                <FormControl
                  control={control}
                  name="product_name"
                  label="产品名称"
                  render={({ field }) => (
                    <Input {...field} value={field.value ?? ''}></Input>
                  )}
                ></FormControl>
                {props.admin && (
                  <FormControl
                    control={control}
                    name="account_name"
                    label="账号名称"
                    render={({ field }) => (
                      <Input {...field} value={field.value ?? ''}></Input>
                    )}
                  ></FormControl>
                )}
                {showTimeRange && (
                  <FormControl
                    control={control}
                    name="time_range"
                    label="统计时间"
                    render={({ field }) => (
                      <TimeRangePicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                )}
                <Button
                  className="mr-2"
                  onClick={onQuery}
                  variant="solid"
                  htmlType="submit"
                  color="primary"
                >
                  查询
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={handleReset}
                >
                  重置
                </Button>
              </Form>
              <div className="flex-auto"></div>
              <div>
                <Button
                  variant="solid"
                  color="primary"
                  icon={<PlusOutlined />}
                  onClick={async () => {
                    openOverlay(<AddScriptModal></AddScriptModal>);
                  }}
                >
                  添加脚本
                </Button>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex-none">标签选择：</div>
              <div className="min-w-0">
                <TagList
                  value={selectedTagIds}
                  onChange={setSelectedTagIds}
                ></TagList>
              </div>
            </div>
          </div>
        )}
        loading={scriptListQueryResult.isFetching}
        showHeader={false}
        dataSource={scriptListQueryResult.data?.history}
        columns={columns}
        pagination={{
          total: scriptListQueryResult.data?.total,
          showTotal: (total) => `共 ${total} 个脚本`,
          current: pagination.page,
          pageSize: pagination.size,
          onChange: (page, pageSize) => {
            changePageNumber(page, pageSize);
            props.onPaginationChange?.({ page, size: pageSize });
          },
        }}
      ></Table>
    </div>
  );
};
